// Every amount of money the feature shows goes through these components or
// through `moneyFont`: in the financial font (theme.fonts.financial, a
// monospace), the words around it in the normal one, as the rest of Rentio
// sets an amount (<TextStyle variation="code"> in PaymentRequestInfo,
// ContractListItem and PaymentOrderRow: the amount with its "€"). An
// amount's own VAT line ("+ € 21,00 btw") is set in the same font as a
// whole, as PaymentOrderRow sets it. A sentence that names an amount (a
// banner, a hint, a tooltip, a confirmation) keeps the normal font, as
// Rentio's own banners do. __tests__/moneyFont.test.ts fails on an amount a
// screen writes past them.

import {
  TextStyle,
  TextStyleVariationTypes,
} from "@rentiohq/web-shared/dist/components";
import React from "react";
import styled, { css } from "styled-components";
import { formatMoney, vatLineText } from "../moneyText.utils";

type TMoneyVariation = Exclude<TextStyleVariationTypes, "code" | "default">;
type TTextStyleProps = React.ComponentProps<typeof TextStyle>;

interface IMoneyStyle {
  // Its weight and color: "strong" for a total, "subdued" for a line that
  // is left out.
  variation?: TMoneyVariation | TMoneyVariation[];
  // A TextStyle size, or "inherit": the size, the weight and the color of
  // the text around it, for a totals line that sets its own.
  size?: TTextStyleProps["size"] | "inherit";
  element?: TTextStyleProps["element"];
}

const variationsOf = (
  variation?: IMoneyStyle["variation"],
): TextStyleVariationTypes[] => [
  "code",
  ...([] as TMoneyVariation[]).concat(variation ?? []),
];

// For a styled-component whose text is an amount (a list's amount column, a
// totals line).
export const moneyFont = css`
  font-family: ${({ theme }) => theme.fonts.financial};
`;

const InheritedMoneyText = styled.span`
  ${moneyFont}
`;

// A text that is an amount with its own words ("+ € 21,00 btw", "€ 80,00
// excl. btw"): all of it in the financial font.
export const MoneyText: React.FC<IMoneyStyle & { children: string }> = ({
  children,
  variation,
  size,
  element,
}) =>
  size === "inherit" ? (
    <InheritedMoneyText as={element}>{children}</InheritedMoneyText>
  ) : (
    <TextStyle
      variation={variationsOf(variation)}
      size={size}
      element={element}
    >
      {children}
    </TextStyle>
  );

// An amount: "€ 1.250,00".
export const Money: React.FC<IMoneyStyle & { amount: number }> = ({
  amount,
  ...style
}) => <MoneyText {...style}>{formatMoney(amount)}</MoneyText>;

// The VAT under an amount, small and subdued: "+ € 21,00 btw".
// Never broken over two lines: "+ € 105,00" over "btw" in a narrow
// column (Lewis, 2026-10-19). The cell grows a little instead.
const NoWrap = styled.div`
  white-space: nowrap;
`;

// A negative VAT (a credit) reads "− € 0,53 btw", not "+ − € 0,53 btw".
export const VatLine: React.FC<{ vat: number }> = ({ vat }) => (
  <NoWrap>
    <MoneyText variation="subdued" size="small" element="div">
      {vatLineText(vat)}
    </MoneyText>
  </NoWrap>
);

// An amount excl. VAT with its VAT under it: the amount cell of the
// feature's cost tables and their totals.
export const MoneyWithVat: React.FC<{
  amount: number;
  vat: number;
  variation?: IMoneyStyle["variation"];
}> = ({ amount, vat, variation }) => (
  <div>
    <span className="amount">
      <Money amount={amount} variation={variation} />
    </span>
    <VatLine vat={vat} />
  </div>
);
